/**
 * The arithmetic behind the "Usage and revenue" card on an application, kept
 * out of the component so it can be reasoned about — and run — on its own.
 *
 * No value imports, deliberately, on the precedent `analytics-window.ts`,
 * `analytics-charts.ts`, `analytics-users.ts` and `analytics-user-usage.ts`
 * set: Node 24 strips types, so a module with only type imports and no JSX
 * runs directly under `node` and the self-check at the foot of this file IS
 * its test suite.
 *
 * Three rules run through all of it, and none of them is cosmetic:
 *
 *   * **A null percentile is not a zero, and it never becomes one.** Every
 *     latency figure on the wire is nullable and a null means "no request in
 *     this slice reached that milestone". Zero milliseconds is a number a
 *     screen draws as an excellent result, so the null survives all the way to
 *     the tile, which renders the words instead. `formatMs` is the only place
 *     a millisecond figure becomes a string and it answers `null` for `null`.
 *   * **A window's percentile is not a percentile of the window.** The rollup
 *     stores one figure per day and the observations are gone, so the window
 *     row carries the traffic-weighted MEAN of the daily figures under a
 *     `basis` of `daily_percentiles_weighted_by_requests`. The card says
 *     "typical day" rather than "p95 over the month", and the day COUNT that
 *     is the mean's denominator is printed beside every figure — a p95 over
 *     one busy Tuesday and a p95 over a month are different claims.
 *   * **No traffic is a MEASUREMENT; withheld money is not.** An application
 *     that earned nothing has a catalogue row of honest zeroes, which is the
 *     single most important row on this surface. Money the reader may not see
 *     is absent rather than zero, and `settlementState` keeps the three cases
 *     — withheld, quiet, moved — from collapsing into each other.
 */

import type {
  AnalyticsApplicationMeasures,
  AnalyticsApplicationRevenue,
  AnalyticsApplicationUsage,
  AnalyticsApplications,
  AnalyticsLatency,
  AnalyticsLatencyBasis,
} from "../admin-client";

/**
 * The row this card is about, found by SLUG.
 *
 * The Admin API answers `?application=<slug>` with the catalogue narrowed to
 * one row, so this is a list of one in practice — and it is still looked up by
 * slug rather than taken at index 0, because "the first row of the answer" and
 * "the application this page is about" are only the same thing while the
 * request and the response agree. They do not agree during the frame after the
 * operator navigates from one application to another with a cached answer
 * still on screen, and drawing App B's figures under App A's heading is the
 * one failure this card must not have.
 */
export function applicationRow(
  report: Pick<AnalyticsApplications, "applications">,
  slug: string,
): AnalyticsApplicationUsage | undefined {
  return report.applications.find((row) => row.application.slug === slug);
}

/**
 * Whether this application was used at all in the window.
 *
 * FALSE does not hide the figures. The tiles still render their zeroes — that
 * is the whole reason the route reads the catalogue rather than the rollup —
 * and what this decides is whether the card states "no traffic in this window"
 * in words beside them. A row of zeros with nothing saying so reads exactly
 * like a card that failed to load, and a card that hid them instead would be
 * hiding its most important answer.
 *
 * NOT the same question as whether the window was MEASURED, which is
 * `windowMeasured(available_from, until)` and is asked first: a window before
 * the sweep ever ran is zeroes too, and "nobody was counting" is a different
 * fact from "nobody used it".
 */
export function hasTraffic(
  m: Pick<AnalyticsApplicationMeasures, "requests" | "total_tokens">,
): boolean {
  return m.requests > 0 || m.total_tokens > 0;
}

/**
 * What the settled money says, in three cases rather than two.
 *
 * `withheld` is the absent `revenue` object — the reader does not hold
 * `wallets:read` — and it must never be reported as "no money moved": that is
 * a claim about the business made out of a fact about the reader.
 *
 * `quiet` is a revenue object whose every figure is zero, which is a real
 * reading and the one an application with no traffic has.
 *
 * The disjunction is over the SEVEN stored figures and not over the three
 * derived ones: `settled_margin_micros` is sell minus cost and is zero for a
 * break-even day, and `net_payments_micros` is zero for a top-up exactly
 * cancelled by a refund. Reading a derived field would call both of those
 * quiet.
 */
export type Settlement = "withheld" | "quiet" | "moved";

export function settlementState(revenue: AnalyticsApplicationRevenue | undefined): Settlement {
  if (revenue === undefined) {
    return "withheld";
  }
  const moved =
    revenue.settled_sell_micros !== 0 ||
    revenue.settled_cost_micros !== 0 ||
    revenue.topup_micros !== 0 ||
    revenue.subscription_charge_micros !== 0 ||
    revenue.refund_micros !== 0 ||
    revenue.paying_users_upper_bound !== 0;
  return moved ? "moved" : "quiet";
}

/** One latency percentile as the card draws it. */
export interface LatencyFigure {
  /** The wire's own key, which is what the tiles are keyed on. */
  key: "p50_first_token_ms" | "p95_first_token_ms" | "p50_total_ms" | "p95_total_ms";
  label: string;
  /** Milliseconds, or `null` for "no request reached that milestone". */
  ms: number | null;
  /**
   * How many days carried a figure for THIS figure's population.
   *
   * The two pairs are taken over different populations — a request that failed
   * before the first token still finished — so the first-token pair reads
   * `first_token_days` and the total pair reads `total_days`. Either can be
   * zero while the other is not, and pointing both at one count would put a
   * month's denominator under a figure taken over one afternoon.
   */
  days: number;
}

/**
 * The four percentiles, in reading order: first token before total, p50 before
 * p95.
 *
 * A fixed order and not a map iteration, because these four sit beside each
 * other on the card and a pair that swapped places between two loads would be
 * a set of numbers an operator cannot compare against the last time they
 * looked.
 */
export function latencyFigures(latency: AnalyticsLatency): LatencyFigure[] {
  return [
    {
      key: "p50_first_token_ms",
      label: "Time to first token (p50)",
      ms: latency.p50_first_token_ms,
      days: latency.first_token_days,
    },
    {
      key: "p95_first_token_ms",
      label: "Time to first token (p95)",
      ms: latency.p95_first_token_ms,
      days: latency.first_token_days,
    },
    {
      key: "p50_total_ms",
      label: "Total response time (p50)",
      ms: latency.p50_total_ms,
      days: latency.total_days,
    },
    {
      key: "p95_total_ms",
      label: "Total response time (p95)",
      ms: latency.p95_total_ms,
      days: latency.total_days,
    },
  ];
}

/**
 * A millisecond figure, or nothing at all.
 *
 * `null` in, `null` out — the tile turns that into the words "not measured",
 * and there is no path through this function that turns an absent measurement
 * into a number. A non-finite figure is treated the same way: it is not a
 * reading either, and `NaN` printed through `toLocaleString` ships as the
 * string "NaN" in a slot the reader expects a duration in.
 */
export function formatMs(ms: number | null): string | null {
  if (ms === null || !Number.isFinite(ms)) {
    return null;
  }
  return `${ms.toLocaleString()} ms`;
}

/** Whether ANY of the four percentiles was measured. False is a card that says
 *  so once, above the four tiles, rather than four tiles each apologising. */
export function latencyMeasured(latency: AnalyticsLatency): boolean {
  return latencyFigures(latency).some((figure) => figure.ms !== null);
}

/**
 * What arithmetic produced the figures, in words.
 *
 * The `basis` member is on the wire precisely so a client never has to know
 * which slice of the document it is holding. The window row is a weighted MEAN
 * of daily percentiles and the sentence says so; a single day's row is the
 * percentile the sweep measured over that day's own requests.
 */
export function latencyBasisLabel(basis: AnalyticsLatencyBasis): string {
  return basis === "measured"
    ? "Measured over that day's own requests."
    : "The traffic-weighted mean of each day's percentile — the typical day, not the window's own percentile, which cannot be recovered from daily figures.";
}

/**
 * The denominator of a weighted mean, said out loud.
 *
 * A p95 is a very different claim over one day and over thirty, and the
 * difference is invisible in the figure itself. `windowDays` is the window's
 * own length off the response, so "3 of 30" is a card telling the operator
 * that twenty-seven days contributed nothing.
 */
export function latencyDaysNote(days: number, windowDays: number): string {
  if (days <= 0) {
    return "No day in this window recorded one.";
  }
  if (days === 1) {
    return `1 of the ${windowDays} days in this window recorded one, so this is a single day rather than a span.`;
  }
  return `${days} of the ${windowDays} days in this window recorded one.`;
}

/* ------------------------------------------------------------------------ *
 * Self-check: `node apps/admin-portal/src/screens/analytics-application-usage.ts`
 * ------------------------------------------------------------------------ */

/** A latency object with only the fields a given check reads. */
function latency(fields: Partial<AnalyticsLatency>): AnalyticsLatency {
  return {
    basis: "daily_percentiles_weighted_by_requests",
    p50_first_token_ms: null,
    p95_first_token_ms: null,
    p50_total_ms: null,
    p95_total_ms: null,
    first_token_days: 0,
    total_days: 0,
    ...fields,
  };
}

/** A window row with only the fields a given check reads. The cast is honest
 *  at the one place it is made: these fixtures never reach a renderer. */
function measures(
  fields: Partial<AnalyticsApplicationMeasures>,
): AnalyticsApplicationMeasures {
  return {
    requests: 0,
    failed_requests: 0,
    failure_rate: 0,
    input_tokens: 0,
    cached_input_tokens: 0,
    cache_write_tokens: 0,
    output_tokens: 0,
    reasoning_tokens: 0,
    total_tokens: 0,
    fallback_requests: 0,
    stream_requests: 0,
    fallback_rate: 0,
    stream_share: 0,
    distinct_users_upper_bound: 0,
    distinct_devices_upper_bound: 0,
    latency: latency({}),
    ...fields,
  };
}

function usage(slug: string, requests: number): AnalyticsApplicationUsage {
  return {
    ...measures({ requests }),
    application: { id: `app-${slug}`, slug, name: `The ${slug} app` },
    status: "active",
    series: [],
  };
}

function revenue(fields: Partial<AnalyticsApplicationRevenue>): AnalyticsApplicationRevenue {
  return {
    settled_sell_micros: 0,
    settled_cost_micros: 0,
    settled_margin_micros: 0,
    topup_micros: 0,
    subscription_charge_micros: 0,
    refund_micros: 0,
    net_payments_micros: 0,
    paying_users_upper_bound: 0,
    ...fields,
  };
}

function selfCheck(): void {
  let passed = 0;
  const check = (name: string, actual: unknown, expected: unknown): void => {
    // A replacer, because JSON.stringify DROPS an undefined-valued key and a
    // withheld money field is exactly an undefined-valued key.
    const show = (value: unknown): string =>
      JSON.stringify(value, (_key, item) => (item === undefined ? "<undefined>" : item)) ??
      "<undefined>";
    if (show(actual) !== show(expected)) {
      throw new Error(
        `analytics-application-usage self-check FAILED: ${name}\n  expected ${show(expected)}\n  actual   ${show(actual)}`,
      );
    }
    passed += 1;
  };

  // ---- applicationRow ---------------------------------------------------
  // Two rows, and the one this card wants is NOT the first: a lookup that
  // took index 0 passes against a list of one and draws the wrong product's
  // figures the moment there are two.
  const report = { applications: [usage("app-a", 10), usage("app-b", 400)] };
  check("applicationRow finds by slug", applicationRow(report, "app-b")?.requests, 400);
  check("applicationRow finds the first one too", applicationRow(report, "app-a")?.requests, 10);
  check("applicationRow on a slug nothing matches", applicationRow(report, "app-c"), undefined);
  check("applicationRow on an empty answer", applicationRow({ applications: [] }, "app-a"), undefined);

  // ---- hasTraffic -------------------------------------------------------
  check("hasTraffic on an application nobody used", hasTraffic(measures({})), false);
  check("hasTraffic on requests alone", hasTraffic(measures({ requests: 1 })), true);
  // The second clause matters on its own: a rollup day that recorded tokens
  // against a zeroed request count is a contradiction worth drawing rather
  // than one worth calling quiet.
  check("hasTraffic on tokens alone", hasTraffic(measures({ total_tokens: 1 })), true);
  // The row this card exists for: every request failed, so there are no
  // tokens at all and there is very much traffic.
  check(
    "hasTraffic on a window of nothing but failures",
    hasTraffic(measures({ requests: 12, failed_requests: 12 })),
    true,
  );

  // ---- settlementState --------------------------------------------------
  check("settlementState with the money withheld", settlementState(undefined), "withheld");
  check("settlementState on an application that settled nothing", settlementState(revenue({})), "quiet");
  check(
    "settlementState on a top-up",
    settlementState(revenue({ topup_micros: 500, net_payments_micros: 500 })),
    "moved",
  );
  check(
    "settlementState on inference revenue alone",
    settlementState(revenue({ settled_sell_micros: 7 })),
    "moved",
  );
  check("settlementState on cost alone", settlementState(revenue({ settled_cost_micros: 7 })), "moved");
  check(
    "settlementState on a subscription charge alone",
    settlementState(revenue({ subscription_charge_micros: 7 })),
    "moved",
  );
  check("settlementState on a refund alone", settlementState(revenue({ refund_micros: 7 })), "moved");
  check(
    "settlementState on payers alone",
    settlementState(revenue({ paying_users_upper_bound: 1 })),
    "moved",
  );
  // The two DERIVED figures, each planted at zero over non-zero operands. A
  // disjunction that read either of them would call both of these quiet.
  check(
    "a break-even month is not quiet",
    settlementState(revenue({ settled_sell_micros: 50, settled_cost_micros: 50 })),
    "moved",
  );
  check(
    "a top-up cancelled by a refund is not quiet",
    settlementState(revenue({ topup_micros: 50, refund_micros: 50 })),
    "moved",
  );

  // ---- latencyFigures ---------------------------------------------------
  // Four DIFFERENT figures and two DIFFERENT day counts, so a builder that
  // read one field twice, or pointed both pairs at one count, reports.
  const four = latency({
    p50_first_token_ms: 120,
    p95_first_token_ms: 400,
    p50_total_ms: 900,
    p95_total_ms: 3000,
    first_token_days: 4,
    total_days: 29,
  });
  check("latencyFigures is the four in reading order", latencyFigures(four), [
    { key: "p50_first_token_ms", label: "Time to first token (p50)", ms: 120, days: 4 },
    { key: "p95_first_token_ms", label: "Time to first token (p95)", ms: 400, days: 4 },
    { key: "p50_total_ms", label: "Total response time (p50)", ms: 900, days: 29 },
    { key: "p95_total_ms", label: "Total response time (p95)", ms: 3000, days: 29 },
  ]);
  // The pair that can be empty while its neighbour is not: every request
  // failed before a token came back, and they all still finished.
  const finishedOnly = latency({ p50_total_ms: 80, p95_total_ms: 90, total_days: 3 });
  check(
    "an unmeasured first-token pair survives as null beside a measured total pair",
    latencyFigures(finishedOnly).map((figure) => figure.ms),
    [null, null, 80, 90],
  );
  check(
    "and it carries its own zero day count, not its neighbour's",
    latencyFigures(finishedOnly).map((figure) => figure.days),
    [0, 0, 3, 3],
  );

  // ---- formatMs ---------------------------------------------------------
  check("formatMs of nothing is nothing", formatMs(null), null);
  // The one that must never happen: an absent measurement drawn as a number
  // the reader would take for an excellent result.
  check("formatMs never invents a zero", formatMs(null) === "0 ms", false);
  check("formatMs of a measured zero IS a zero", formatMs(0), "0 ms");
  check("formatMs of a figure", formatMs(950), "950 ms");
  // Locale-independent: the separator is the runtime's, the unit is ours.
  check("formatMs separates a big figure and keeps the unit", formatMs(12345)?.endsWith(" ms"), true);
  check("formatMs of NaN is not a reading", formatMs(Number.NaN), null);
  check("formatMs of Infinity is not a reading", formatMs(Number.POSITIVE_INFINITY), null);

  // ---- latencyMeasured --------------------------------------------------
  check("latencyMeasured with nothing measured", latencyMeasured(latency({})), false);
  check("latencyMeasured on one figure alone", latencyMeasured(latency({ p95_total_ms: 1 })), true);
  // A measured ZERO is a measurement. `some(figure => figure.ms)` would call
  // this unmeasured, which is the mutation this line exists to catch.
  check("a measured zero counts as measured", latencyMeasured(latency({ p50_total_ms: 0 })), true);

  // ---- latencyBasisLabel ------------------------------------------------
  check(
    "the window's basis says it is a mean of daily figures",
    latencyBasisLabel("daily_percentiles_weighted_by_requests").includes("weighted mean"),
    true,
  );
  check(
    "and that it is not the window's own percentile",
    latencyBasisLabel("daily_percentiles_weighted_by_requests").includes("not the window's own"),
    true,
  );
  check(
    "a measured day says it was measured",
    latencyBasisLabel("measured"),
    "Measured over that day's own requests.",
  );

  // ---- latencyDaysNote --------------------------------------------------
  check("no day recorded one", latencyDaysNote(0, 30), "No day in this window recorded one.");
  check(
    "one day is called out as one day",
    latencyDaysNote(1, 30),
    "1 of the 30 days in this window recorded one, so this is a single day rather than a span.",
  );
  check("several days", latencyDaysNote(7, 30), "7 of the 30 days in this window recorded one.");
  // A negative count cannot arrive from the wire, and answering "-1 of 30
  // days" if it did would be the card repeating a number it should refuse.
  check("a negative count reads as none", latencyDaysNote(-1, 30), "No day in this window recorded one.");

  console.log(`analytics-application-usage self-check: ${passed} checks passed`);
}

if ((import.meta as unknown as { main?: boolean }).main === true) {
  selfCheck();
}
